import { useEffect, useRef } from 'react';

interface ChartData {
  month: string;
  revenue: number;
  leads: number;
}

interface ChartsProps {
  data: ChartData[];
}

export default function Charts({ data }: ChartsProps) {
  const maxRevenue = Math.max(...data.map(d => d.revenue));
  const maxLeads = Math.max(...data.map(d => d.leads));

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Revenue & Leads Overview</h3>
      
      {/* Revenue Bar Chart */}
      <div className="mb-8">
        <h4 className="text-sm font-medium text-gray-500 mb-4">Monthly Revenue</h4>
        <div className="flex items-end justify-between h-48 gap-2">
          {data.map((item, index) => (
            <div key={index} className="flex-1 flex flex-col items-center">
              <div className="w-full flex flex-col items-center gap-1">
                <span className="text-xs text-gray-500 mb-1">
                  ${(item.revenue / 1000).toFixed(0)}k
                </span>
                <div
                  className="w-full bg-gradient-to-t from-indigo-600 to-indigo-400 rounded-t-md transition-all duration-500 hover:from-indigo-700 hover:to-indigo-500"
                  style={{ height: `${(item.revenue / maxRevenue) * 140}px` }}
                />
              </div>
              <span className="text-xs text-gray-400 mt-2">{item.month}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Leads Line Chart */}
      <div>
        <h4 className="text-sm font-medium text-gray-500 mb-4">Leads Trend</h4>
        <div className="relative h-32">
          <svg className="w-full h-full" viewBox="0 0 100 50" preserveAspectRatio="none">
            {/* Grid lines */}
            <line x1="0" y1="10" x2="100" y2="10" stroke="#f3f4f6" strokeWidth="0.5" />
            <line x1="0" y1="25" x2="100" y2="25" stroke="#f3f4f6" strokeWidth="0.5" />
            <line x1="0" y1="40" x2="100" y2="40" stroke="#f3f4f6" strokeWidth="0.5" />
            
            {/* Line */}
            <path
              d={`M ${data.map((d, i) => `${(i / (data.length - 1)) * 100} ${50 - (d.leads / maxLeads) * 45}`).join(' L ')}`}
              fill="none"
              stroke="#10b981"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            
            {/* Area under line */}
            <path
              d={`M 0 50 L ${data.map((d, i) => `${(i / (data.length - 1)) * 100} ${50 - (d.leads / maxLeads) * 45}`).join(' L ')} L 100 50 Z`}
              fill="url(#gradient)"
              opacity="0.3"
            />
            
            {/* Data points */}
            {data.map((d, i) => (
              <circle
                key={i}
                cx={(i / (data.length - 1)) * 100}
                cy={50 - (d.leads / maxLeads) * 45}
                r="1.5"
                fill="#10b981"
              />
            ))}
            
            <defs>
              <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
          
          {/* X-axis labels */}
          <div className="flex justify-between mt-1">
            {data.map((item, index) => (
              <span key={index} className="text-xs text-gray-400">{item.month}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Pipeline Chart Component
interface PipelineChartProps {
  stages: { name: string; value: number; color: string }[];
}

export function PipelineChart({ stages }: PipelineChartProps) {
  const total = stages.reduce((sum, s) => sum + s.value, 0);

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Sales Pipeline</h3>
      
      <div className="space-y-4">
        {stages.map((stage, index) => (
          <div key={index} className="group">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">{stage.name}</span>
              <span className="text-sm text-gray-500">{stage.value} deals</span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 group-hover:opacity-80"
                style={{
                  width: `${(stage.value / total) * 100}%`,
                  backgroundColor: stage.color,
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Total Pipeline Value</span>
          <span className="text-lg font-semibold text-gray-900">${total.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}
